/**
 * Government contracting — add / configure. Lets owner/office seed the tracking
 * queue: solicitations (opportunities), certifications, awarded contracts, and
 * per-contract compliance requirements. Mutations are gated server-side; a 403
 * surfaces as an error here.
 *
 * LEGAL / SAFETY: reference & tracking only (master §6.13). BEAM never bids,
 * submits, or files — these forms only record data the org chooses to track.
 * NEVER imports @smartbox/* (mobile invariant 1). No raw style values.
 */
import { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listContracts,
  listSolicitations,
  createSolicitation,
  createCertification,
  createContract,
  createComplianceRequirement,
  createFederalRegistration,
  createProposal,
  type CreateSolicitationRequest,
  type CreateCertificationRequest,
  type CreateContractRequest,
  type CreateComplianceRequirementRequest,
  type CreateFederalRegistrationRequest,
  type CreateProposalRequest,
  type FederalRegistrationStatus,
  type ProposalStatus,
  type CertificationType,
  type SolicitationSource,
  type SetAsideType,
  type ContractRow,
  type SolicitationRow,
} from '../../../src/api/gov';
import { Screen, Text, Card, Button, Field, Select } from '../../../src/components/ui';
import { theme } from '../../../src/theme';
import { extractErrorMessage } from '../../../src/lib/format';

type Entity = 'solicitation' | 'certification' | 'contract' | 'compliance' | 'registration' | 'proposal';

const ENTITIES: { value: Entity; label: string }[] = [
  { value: 'solicitation', label: 'Solicitation' },
  { value: 'registration', label: 'Federal registration' },
  { value: 'proposal', label: 'Proposal' },
  { value: 'certification', label: 'Certification' },
  { value: 'contract', label: 'Contract' },
  { value: 'compliance', label: 'Compliance' },
];

const REG_STATUSES: { value: FederalRegistrationStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
];

const PROPOSAL_STATUSES: { value: ProposalStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'internal_review', label: 'Internal review' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
];

const SOURCES: { value: SolicitationSource; label: string }[] = [
  { value: 'sam_gov', label: 'SAM.gov' },
  { value: 'state_portal', label: 'State portal' },
  { value: 'local', label: 'Local' },
  { value: 'prime_subcontract', label: 'Prime / subcontract' },
];
const SET_ASIDES: { value: SetAsideType; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'small_business', label: 'Small business' },
  { value: 'wosb', label: 'WOSB' },
  { value: 'edwosb', label: 'EDWOSB' },
  { value: 'c8a', label: '8(a)' },
];
const CERT_TYPES: { value: CertificationType; label: string }[] = [
  { value: 'wosb', label: 'WOSB' },
  { value: 'edwosb', label: 'EDWOSB' },
  { value: 'sba_small_business', label: 'SBA Small Business' },
  { value: 'c8a', label: '8(a)' },
  { value: 'hubzone', label: 'HUBZone' },
  { value: 'vosb', label: 'VOSB' },
  { value: 'state_dbe', label: 'State DBE' },
  { value: 'other', label: 'Other' },
];

/** Convert a YYYY-MM-DD field value into an ISO datetime, or undefined if blank/invalid. */
function toIso(v: string): string | undefined {
  const t = v.trim();
  if (!t) return undefined;
  const d = new Date(`${t}T00:00:00.000Z`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

export default function GovAddScreen() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [entity, setEntity] = useState<Entity>('solicitation');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const invalidate = (keys: string[]) => keys.forEach((k) => void queryClient.invalidateQueries({ queryKey: [k] }));

  // --- Solicitation ---
  const [sSource, setSSource] = useState<SolicitationSource>('sam_gov');
  const [sTitle, setSTitle] = useState('');
  const [sNumber, setSNumber] = useState('');
  const [sAgency, setSAgency] = useState('');
  const [sNaics, setSNaics] = useState('');
  const [sSetAside, setSSetAside] = useState<SetAsideType>('none');
  const [sDue, setSDue] = useState('');
  const [sValue, setSValue] = useState('');
  const [sUrl, setSUrl] = useState('');

  const solicitationMut = useMutation({
    mutationFn: (body: CreateSolicitationRequest) => createSolicitation(body),
    onSuccess: () => { invalidate(['gov-solicitations']); setDone('Solicitation added to the queue.'); setError(null); resetSolicitation(); },
    onError: (err) => setError(extractErrorMessage(err, 'Could not add the solicitation.')),
  });
  const resetSolicitation = () => { setSTitle(''); setSNumber(''); setSAgency(''); setSNaics(''); setSDue(''); setSValue(''); setSUrl(''); };

  // --- Certification ---
  const [cType, setCType] = useState<CertificationType>('wosb');
  const [cBody, setCBody] = useState('');
  const [cNumber, setCNumber] = useState('');
  const [cIssued, setCIssued] = useState('');
  const [cExpires, setCExpires] = useState('');

  const certificationMut = useMutation({
    mutationFn: (body: CreateCertificationRequest) => createCertification(body),
    onSuccess: () => { invalidate(['gov-certifications', 'gov-certifications-expiring']); setDone('Certification saved.'); setError(null); setCBody(''); setCNumber(''); setCIssued(''); setCExpires(''); },
    onError: (err) => setError(extractErrorMessage(err, 'Could not save the certification.')),
  });

  // --- Contract ---
  const [ctNumber, setCtNumber] = useState('');
  const [ctAgency, setCtAgency] = useState('');
  const [ctAmount, setCtAmount] = useState('');
  const [ctStart, setCtStart] = useState('');
  const [ctEnd, setCtEnd] = useState('');

  const contractMut = useMutation({
    mutationFn: (body: CreateContractRequest) => createContract(body),
    onSuccess: () => { invalidate(['gov-contracts']); setDone('Contract saved.'); setError(null); setCtNumber(''); setCtAgency(''); setCtAmount(''); setCtStart(''); setCtEnd(''); },
    onError: (err) => setError(extractErrorMessage(err, 'Could not save the contract.')),
  });

  // --- Compliance requirement ---
  const contractsQuery = useQuery({ queryKey: ['gov-contracts'], queryFn: listContracts });
  const [rContract, setRContract] = useState<string | null>(null);
  const [rText, setRText] = useState('');
  const [rDue, setRDue] = useState('');

  const complianceMut = useMutation({
    mutationFn: (body: CreateComplianceRequirementRequest) => createComplianceRequirement(body),
    onSuccess: () => { invalidate(['gov-compliance']); setDone('Compliance requirement added.'); setError(null); setRText(''); setRDue(''); },
    onError: (err) => setError(extractErrorMessage(err, 'Could not add the compliance requirement.')),
  });

  // --- Federal registration ---
  const [gUei, setGUei] = useState('');
  const [gCage, setGCage] = useState('');
  const [gStatus, setGStatus] = useState<FederalRegistrationStatus>('pending');
  const [gNaics, setGNaics] = useState('');
  const [gRegDate, setGRegDate] = useState('');
  const [gExpDate, setGExpDate] = useState('');
  const [gSamUrl, setGSamUrl] = useState('');

  const registrationMut = useMutation({
    mutationFn: (body: CreateFederalRegistrationRequest) => createFederalRegistration(body),
    onSuccess: () => { invalidate(['gov-registrations']); setDone('Federal registration saved.'); setError(null); setGUei(''); setGCage(''); setGNaics(''); setGRegDate(''); setGExpDate(''); setGSamUrl(''); },
    onError: (err) => setError(extractErrorMessage(err, 'Could not save the federal registration.')),
  });

  // --- Proposal ---
  const solicitationsQuery = useQuery({ queryKey: ['gov-solicitations'], queryFn: listSolicitations });
  const [pSolicitation, setPSolicitation] = useState<string | null>(null);
  const [pStatus, setPStatus] = useState<ProposalStatus>('draft');
  const [pVersion, setPVersion] = useState('');
  const [pNotes, setPNotes] = useState('');

  const proposalMut = useMutation({
    mutationFn: (body: CreateProposalRequest) => createProposal(body),
    onSuccess: () => { invalidate(['gov-proposals']); setDone('Proposal drafted.'); setError(null); setPVersion(''); setPNotes(''); },
    onError: (err) => setError(extractErrorMessage(err, 'Could not draft the proposal.')),
  });

  const busy = solicitationMut.isPending || certificationMut.isPending || contractMut.isPending || complianceMut.isPending || registrationMut.isPending || proposalMut.isPending;

  const onSubmit = () => {
    setError(null);
    setDone(null);
    if (entity === 'solicitation') {
      if (!sTitle.trim()) return setError('Enter a solicitation title.');
      const body: CreateSolicitationRequest = { source: sSource, title: sTitle.trim(), set_aside_type: sSetAside };
      if (sNumber.trim()) body.solicitation_number = sNumber.trim();
      if (sAgency.trim()) body.issuing_agency = sAgency.trim();
      if (sNaics.trim()) body.naics_code = sNaics.trim();
      const due = toIso(sDue); if (due) body.response_due = due;
      const val = Number.parseFloat(sValue); if (sValue.trim() && Number.isFinite(val)) body.estimated_value = val;
      if (sUrl.trim()) body.source_url = sUrl.trim();
      solicitationMut.mutate(body);
    } else if (entity === 'certification') {
      const body: CreateCertificationRequest = { certification_type: cType };
      if (cBody.trim()) body.certifying_body = cBody.trim();
      if (cNumber.trim()) body.certificate_number = cNumber.trim();
      const issued = toIso(cIssued); if (issued) body.issued_date = issued;
      const expires = toIso(cExpires); if (expires) body.expiration_date = expires;
      certificationMut.mutate(body);
    } else if (entity === 'contract') {
      const body: CreateContractRequest = {};
      if (ctNumber.trim()) body.contract_number = ctNumber.trim();
      if (ctAgency.trim()) body.awarding_agency = ctAgency.trim();
      const amt = Number.parseFloat(ctAmount); if (ctAmount.trim() && Number.isFinite(amt)) body.award_amount = amt;
      const start = toIso(ctStart); if (start) body.period_start = start;
      const end = toIso(ctEnd); if (end) body.period_end = end;
      if (!body.contract_number && !body.awarding_agency) return setError('Enter at least a contract number or awarding agency.');
      contractMut.mutate(body);
    } else if (entity === 'compliance') {
      if (!rContract) return setError('Choose the contract this requirement belongs to.');
      if (!rText.trim()) return setError('Enter the requirement.');
      const body: CreateComplianceRequirementRequest = { gov_contract_id: rContract, requirement: rText.trim() };
      const due = toIso(rDue); if (due) body.due_date = due;
      complianceMut.mutate(body);
    } else if (entity === 'registration') {
      const body: CreateFederalRegistrationRequest = { registration_status: gStatus };
      if (gUei.trim()) body.uei = gUei.trim();
      if (gCage.trim()) body.cage_code = gCage.trim();
      if (gNaics.trim()) body.primary_naics = gNaics.trim();
      const reg = toIso(gRegDate); if (reg) body.registration_date = reg;
      const exp = toIso(gExpDate); if (exp) body.expiration_date = exp;
      if (gSamUrl.trim()) body.sam_profile_url = gSamUrl.trim();
      if (!body.uei && !body.cage_code) return setError('Enter at least a UEI or CAGE code.');
      registrationMut.mutate(body);
    } else {
      if (!pSolicitation) return setError('Choose the solicitation this proposal responds to.');
      const body: CreateProposalRequest = { solicitation_id: pSolicitation, status: pStatus };
      const v = Number.parseInt(pVersion, 10); if (pVersion.trim() && Number.isFinite(v)) body.version = v;
      if (pNotes.trim()) body.outcome_notes = pNotes.trim();
      proposalMut.mutate(body);
    }
  };

  const contracts = contractsQuery.data ?? [];
  const solicitations = solicitationsQuery.data ?? [];

  return (
    <Screen scroll>
      <Text variant="title">Add / configure</Text>
      <Card>
        <Text variant="small" color="secondary">
          Reference & tracking only — BEAM never bids, submits, or files. These forms record
          opportunities and obligations you choose to track.
        </Text>
      </Card>

      <Text variant="label" color="secondary" style={styles.fieldLabel}>What are you adding?</Text>
      <Select options={ENTITIES} value={entity} onChange={(v) => { setEntity(v); setError(null); setDone(null); }} />

      {entity === 'solicitation' ? (
        <Card>
          <Text variant="bodyStrong">Solicitation</Text>
          <Text variant="label" color="secondary" style={styles.fieldLabel}>Source</Text>
          <Select options={SOURCES} value={sSource} onChange={setSSource} />
          <Field label="Title" placeholder="e.g. Roof replacement — City Hall" value={sTitle} onChangeText={setSTitle} accessibilityLabel="Solicitation title" />
          <Field label="Solicitation number (optional)" placeholder="e.g. W912-24-R-0001" value={sNumber} onChangeText={setSNumber} autoCapitalize="characters" autoCorrect={false} accessibilityLabel="Solicitation number" />
          <Field label="Issuing agency (optional)" placeholder="e.g. GSA" value={sAgency} onChangeText={setSAgency} accessibilityLabel="Issuing agency" />
          <Field label="NAICS code (optional)" placeholder="e.g. 236220" value={sNaics} onChangeText={setSNaics} keyboardType="number-pad" accessibilityLabel="NAICS code" />
          <Text variant="label" color="secondary" style={styles.fieldLabel}>Set-aside</Text>
          <Select options={SET_ASIDES} value={sSetAside} onChange={setSSetAside} />
          <Field label="Response due (optional, YYYY-MM-DD)" placeholder="2026-07-15" value={sDue} onChangeText={setSDue} autoCapitalize="none" autoCorrect={false} accessibilityLabel="Response due date" />
          <Field label="Estimated value (optional)" placeholder="0.00" value={sValue} onChangeText={setSValue} keyboardType="decimal-pad" accessibilityLabel="Estimated value" />
          <Field label="Source URL (optional)" placeholder="https://sam.gov/…" value={sUrl} onChangeText={setSUrl} autoCapitalize="none" autoCorrect={false} accessibilityLabel="Source URL" />
        </Card>
      ) : null}

      {entity === 'certification' ? (
        <Card>
          <Text variant="bodyStrong">Certification</Text>
          <Text variant="label" color="secondary" style={styles.fieldLabel}>Type</Text>
          <Select options={CERT_TYPES} value={cType} onChange={setCType} />
          <Field label="Certifying body (optional)" placeholder="e.g. SBA" value={cBody} onChangeText={setCBody} accessibilityLabel="Certifying body" />
          <Field label="Certificate number (optional)" placeholder="e.g. 123456" value={cNumber} onChangeText={setCNumber} autoCapitalize="characters" autoCorrect={false} accessibilityLabel="Certificate number" />
          <Field label="Issued date (optional, YYYY-MM-DD)" placeholder="2025-01-01" value={cIssued} onChangeText={setCIssued} autoCapitalize="none" autoCorrect={false} accessibilityLabel="Issued date" />
          <Field label="Expiration date (optional, YYYY-MM-DD)" placeholder="2028-01-01" value={cExpires} onChangeText={setCExpires} autoCapitalize="none" autoCorrect={false} accessibilityLabel="Expiration date" />
        </Card>
      ) : null}

      {entity === 'contract' ? (
        <Card>
          <Text variant="bodyStrong">Awarded contract</Text>
          <Field label="Contract number" placeholder="e.g. W912-24-C-0099" value={ctNumber} onChangeText={setCtNumber} autoCapitalize="characters" autoCorrect={false} accessibilityLabel="Contract number" />
          <Field label="Awarding agency" placeholder="e.g. US Army Corps of Engineers" value={ctAgency} onChangeText={setCtAgency} accessibilityLabel="Awarding agency" />
          <Field label="Award amount (optional)" placeholder="0.00" value={ctAmount} onChangeText={setCtAmount} keyboardType="decimal-pad" accessibilityLabel="Award amount" />
          <Field label="Period start (optional, YYYY-MM-DD)" placeholder="2026-08-01" value={ctStart} onChangeText={setCtStart} autoCapitalize="none" autoCorrect={false} accessibilityLabel="Period start" />
          <Field label="Period end (optional, YYYY-MM-DD)" placeholder="2027-07-31" value={ctEnd} onChangeText={setCtEnd} autoCapitalize="none" autoCorrect={false} accessibilityLabel="Period end" />
        </Card>
      ) : null}

      {entity === 'registration' ? (
        <Card>
          <Text variant="bodyStrong">Federal registration</Text>
          <Field label="UEI (optional)" placeholder="e.g. ABC123DEF456" value={gUei} onChangeText={setGUei} autoCapitalize="characters" autoCorrect={false} accessibilityLabel="UEI" />
          <Field label="CAGE code (optional)" placeholder="e.g. 1ABC2" value={gCage} onChangeText={setGCage} autoCapitalize="characters" autoCorrect={false} accessibilityLabel="CAGE code" />
          <Text variant="label" color="secondary" style={styles.fieldLabel}>Status</Text>
          <Select options={REG_STATUSES} value={gStatus} onChange={setGStatus} />
          <Field label="Primary NAICS (optional)" placeholder="e.g. 236220" value={gNaics} onChangeText={setGNaics} keyboardType="number-pad" accessibilityLabel="Primary NAICS" />
          <Field label="Registration date (optional, YYYY-MM-DD)" placeholder="2026-01-01" value={gRegDate} onChangeText={setGRegDate} autoCapitalize="none" autoCorrect={false} accessibilityLabel="Registration date" />
          <Field label="Expiration date (optional, YYYY-MM-DD)" placeholder="2027-01-01" value={gExpDate} onChangeText={setGExpDate} autoCapitalize="none" autoCorrect={false} accessibilityLabel="Expiration date" />
          <Field label="SAM profile URL (optional)" placeholder="https://sam.gov/…" value={gSamUrl} onChangeText={setGSamUrl} autoCapitalize="none" autoCorrect={false} accessibilityLabel="SAM profile URL" />
        </Card>
      ) : null}

      {entity === 'proposal' ? (
        <Card>
          <Text variant="bodyStrong">Proposal</Text>
          {solicitationsQuery.isLoading ? (
            <Text color="secondary">Loading solicitations…</Text>
          ) : solicitations.length === 0 ? (
            <Text color="secondary">Add a solicitation first — a proposal responds to one.</Text>
          ) : (
            <>
              <Text variant="label" color="secondary" style={styles.fieldLabel}>Solicitation</Text>
              <Select
                options={solicitations.map((s: SolicitationRow) => ({ value: s.id, label: s.title }))}
                value={pSolicitation}
                onChange={setPSolicitation}
              />
              <Text variant="label" color="secondary" style={styles.fieldLabel}>Status</Text>
              <Select options={PROPOSAL_STATUSES} value={pStatus} onChange={setPStatus} />
              <Field label="Version (optional)" placeholder="1" value={pVersion} onChangeText={setPVersion} keyboardType="number-pad" accessibilityLabel="Proposal version" />
              <Field label="Notes (optional)" placeholder="Internal notes" value={pNotes} onChangeText={setPNotes} multiline accessibilityLabel="Proposal notes" />
            </>
          )}
        </Card>
      ) : null}

      {entity === 'compliance' ? (
        <Card>
          <Text variant="bodyStrong">Compliance requirement</Text>
          {contracts.length === 0 ? (
            <Text color="secondary">Add an awarded contract first — compliance requirements attach to a contract.</Text>
          ) : (
            <>
              <Text variant="label" color="secondary" style={styles.fieldLabel}>Contract</Text>
              <Select
                options={contracts.map((c: ContractRow) => ({ value: c.id, label: c.contract_number || c.awarding_agency || c.id.slice(0, 8).toUpperCase() }))}
                value={rContract}
                onChange={setRContract}
              />
              <Field label="Requirement" placeholder="e.g. Submit monthly DBE participation report" value={rText} onChangeText={setRText} multiline accessibilityLabel="Requirement" />
              <Field label="Due date (optional, YYYY-MM-DD)" placeholder="2026-09-01" value={rDue} onChangeText={setRDue} autoCapitalize="none" autoCorrect={false} accessibilityLabel="Due date" />
            </>
          )}
        </Card>
      ) : null}

      {error ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">{error}</Text>
        </Card>
      ) : null}
      {done ? (
        <Card>
          <Text variant="bodyStrong" color="primary" accessibilityRole="alert">{done}</Text>
        </Card>
      ) : null}

      <View style={styles.actions}>
        <Button title="Save" onPress={onSubmit} loading={busy} disabled={busy} />
        <Button title="Back to government" variant="secondary" onPress={() => router.replace('/(app)/government')} disabled={busy} />
      </View>
    </Screen>
  );
}

const styles = StyleSheet.create({
  fieldLabel: { marginTop: theme.spacing.sm, marginBottom: theme.spacing.xs },
  actions: { gap: theme.spacing.sm, marginTop: theme.spacing.md },
});
